/**
 * 画面共通の小さなフック。外部ストアは useSyncExternalStore で読む（effect で写さない）。
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { viewpoint, type ViewpointState } from "./viewpoint.js";

export function useViewpoint(): ViewpointState {
  return useSyncExternalStore(viewpoint.subscribe, () => viewpoint.state);
}

/** 読みに使う基準日（選ばれていなければ今日）と、それを変える関数。 */
export function useAsOf(): [string, (d: string | null) => void] {
  const s = useViewpoint();
  return [s.asOf ?? viewpoint.effectiveAsOf(), viewpoint.setAsOf];
}

/** 「最新に戻る」「自分の書き込みの後」に一覧を読み直す。 */
export function useReloadOnReset(load: () => void): void {
  const ref = useRef(load);
  ref.current = load;
  useEffect(() => viewpoint.subscribeReset(() => ref.current()), []);
}

function subscribeOnline(cb: () => void): () => void {
  addEventListener("online", cb);
  addEventListener("offline", cb);
  return () => { removeEventListener("online", cb); removeEventListener("offline", cb); };
}
export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

/** 毎分更新する「今」（期限の相対表現・時点の帯の判定用）。 */
export function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback((cb: () => void) => {
    const m = matchMedia(query);
    m.addEventListener("change", cb);
    return () => m.removeEventListener("change", cb);
  }, [query]);
  return useSyncExternalStore(subscribe, () => matchMedia(query).matches, () => false);
}

/**
 * データの読み込み（一覧・詳細）。読み込み中・失敗・再読み込みを同じ形で扱う。
 * 失敗してもページ全体を白くしない（その部分に ErrorPanel を出す）。
 * key が変わったら読み直す。reload() は手で読み直す（「再読み込み」ボタン）。
 */
export function useLoad<T>(fetcher: () => Promise<T>, key: unknown[]): {
  data: T | null; error: unknown; loading: boolean; reload: () => void; setData: (fn: (d: T | null) => T | null) => void;
} {
  const [state, setState] = useState<{ data: T | null; error: unknown; loading: boolean }>({ data: null, error: null, loading: true });
  const [tick, setTick] = useState(0);
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchRef.current()
      .then((data) => { if (alive) setState({ data, error: null, loading: false }); })
      .catch((error: unknown) => { if (alive) setState((s) => ({ data: s.data, error, loading: false })); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...key, tick]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  const setData = useCallback((fn: (d: T | null) => T | null) => setState((s) => ({ ...s, data: fn(s.data) })), []);
  return { ...state, reload, setData };
}
